import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import Error403 from '../Error/403';
import * as XLSX from 'xlsx';

const QuizView = ({ ...rest }) => {
  const [quizList, setQuizList] = useState([]);
  const [quizType, setQuizType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [assessmentType, setAssessmentType] = useState('all');
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_API_URL}/api/quizdetails/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!isMounted) return;
        if (data.detail) {
          setError('Failed to load assessments');
        } else {
          const activeQuizzes = data.filter((item) => item.status === 'Active');
          setQuizList(activeQuizzes.map((item) => item.name));
          setQuizType(
            activeQuizzes.map((item) => ({
              name: item.name,
              organization_type: item.organization_type,
            }))
          );
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError('Failed to load assessments. Please try again.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAssessmentTypeChange = useCallback(
    (event, newValue) => {
      if (newValue !== null) {
        setAssessmentType(newValue);
        setPage(0);
      }
    },
    []
  );

  const filteredByType = useMemo(() => {
    if (assessmentType === 'all') return quizList;
    return quizType
      .filter((item) => {
        if (assessmentType === 'common') return item.organization_type === 'Common';
        if (assessmentType === 'individual') return item.organization_type === 'Individual';
        return true;
      })
      .map((item) => item.name);
  }, [quizList, quizType, assessmentType]);

  const filteredQuizList = useMemo(() => {
    if (!searchTerm.trim()) return filteredByType;
    const searchLower = searchTerm.toLowerCase();
    return filteredByType.filter((quiz) => quiz.toLowerCase().includes(searchLower));
  }, [filteredByType, searchTerm]);

  const paginatedQuizList = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredQuizList.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredQuizList, page, rowsPerPage]);

  const stats = useMemo(() => {
    const common = quizType.filter((item) => item.organization_type === 'Common').length;
    const individual = quizType.filter((item) => item.organization_type === 'Individual').length;
    return {
      total: quizList.length,
      common,
      individual,
      visible: filteredQuizList.length,
    };
  }, [quizList.length, quizType, filteredQuizList.length]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setPage(0);
  }, []);

  const saveExcelFile = useCallback((buffer, filename) => {
    const data = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, []);

  const handleDownload = useCallback(
    (quizName) => {
      setDownloadLoading(quizName);
      setError(null);

      fetch(`${process.env.REACT_APP_API_URL}/api/quizdetailbyassessementname/${quizName}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data.detail) {
            setError('Failed to download assessment questions');
          } else {
            const excelData = data[0].quizdetails.map((row) => Object.values(row));
            const worksheet = XLSX.utils.aoa_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Quiz Questions');

            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            saveExcelFile(excelBuffer, `${quizName}.xlsx`);
            setDownloadSuccess(true);
          }
        })
        .catch((err) => {
          setError('Failed to download assessment. Please try again.');
        })
        .finally(() => {
          setDownloadLoading(null);
        });
    },
    [saveExcelFile]
  );

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setDownloadSuccess(false);
  }, []);

  const renderSkeleton = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="text" width="80%" height={32} sx={{ mx: 'auto', mb: 2 }} />
                <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto' }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    ),
    []
  );

  const renderEmptyState = useMemo(
    () => (
      <Box
        sx={{
          mt: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <QuizIcon sx={{ fontSize: 64, mb: 1, color: 'text.secondary' }} />
        <Typography variant="h6">No assessments available</Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 360, mt: 1 }}>
          {searchTerm || assessmentType !== 'all'
            ? 'No assessments match your search or filter. Try adjusting your criteria.'
            : 'No active assessments found. Create assessments to get started.'}
        </Typography>
      </Box>
    ),
    [searchTerm, assessmentType]
  );

  return (
    <>
      <Helmet>
        <title>LabAsService - View Assessment Questions</title>
      </Helmet>
      {localStorage.getItem('user_access') === 'You do not have permission to perform this action.' &&
      localStorage.getItem('group') !== 'trainer' &&
      localStorage.getItem('group') !== 'admin' ? (
        <Error403 />
      ) : (
        <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
          <Card {...rest} sx={{ mb: 4 }}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" component="h1">
                    View Assessment Questions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse and download assessment questions for all active assessments
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Grid container spacing={2} sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
              <Grid item xs={12} md={6} lg={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1} alignItems="flex-start">
                      <QuizIcon color="primary" fontSize="large" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Total assessments
                      </Typography>
                      <Typography variant="h4">{stats.total}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`${stats.common} public`} color="info" size="small" />
                        <Chip label={`${stats.individual} customer owned`} color="primary" size="small" />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Filter assessments</Typography>
                  <ToggleButtonGroup
                    value={assessmentType}
                    exclusive
                    onChange={handleAssessmentTypeChange}
                    size="small"
                    sx={{
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="all">
                      All ({stats.total})
                    </ToggleButton>
                    <ToggleButton value="common">
                      <PublicIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Public ({stats.common})
                    </ToggleButton>
                    <ToggleButton value="individual">
                      <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Customer Owned ({stats.individual})
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {stats.visible} assessment{stats.visible !== 1 ? 's' : ''} found
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search by Assessment Name"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: '100%', sm: 320 } }}
                />
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                renderSkeleton
              ) : paginatedQuizList.length > 0 ? (
                <>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {paginatedQuizList.map((quiz) => {
                      const quizInfo = quizType.find((item) => item.name === quiz);
                      const isCommon = quizInfo?.organization_type === 'Common';
                      const isDownloading = downloadLoading === quiz;

                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={quiz}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderColor: 'primary.light',
                              borderRadius: 2,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                boxShadow: 2,
                                borderColor: 'primary.main',
                              },
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                              <Stack spacing={1.5} alignItems="center">
                                <Typography
                                  variant="h6"
                                  align="center"
                                  color="textPrimary"
                                  sx={{ wordBreak: 'break-word', fontWeight: 500 }}
                                >
                                  {quiz}
                                </Typography>
                                {isCommon ? (
                                  <Chip
                                    icon={<PublicIcon sx={{ fontSize: 16 }} />}
                                    label="Public Assessment"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                                    label="Customer Owned"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2, justifyContent: 'center' }}>
                              <Tooltip title="Download assessment questions as Excel" arrow>
                                <span>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={() => handleDownload(quiz)}
                                    disabled={isDownloading}
                                    sx={{ borderRadius: '50px', px: 2 }}
                                    fullWidth
                                  >
                                    {isDownloading ? 'Downloading...' : 'Download'}
                                  </Button>
                                </span>
                              </Tooltip>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {filteredQuizList.length > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                      <TablePagination
                        rowsPerPageOptions={[12, 24, 48, 96]}
                        component="div"
                        count={filteredQuizList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                          '& .MuiTablePagination-toolbar': {
                            justifyContent: 'flex-start',
                            paddingLeft: 2,
                          },
                          '& .MuiTablePagination-spacer': {
                            display: 'none',
                          },
                          '& .MuiTablePagination-selectLabel': {
                            margin: 0,
                          },
                          '& .MuiTablePagination-displayedRows': {
                            margin: 0,
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                renderEmptyState
              )}
            </Box>
          </Card>

          <Snackbar
            open={downloadSuccess}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              onClose={handleSnackbarClose}
              severity="success"
              sx={{ fontSize: '16px' }}
            >
              Assessment downloaded successfully!
            </MuiAlert>
          </Snackbar>
        </Container>
      )}
    </>
  );
};

export default QuizView;
